import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SkeletonModule } from 'primeng/skeleton';
import {
  FilterField,
  FilterOperator,
  FindOptions,
  TicketLog as LegacyTicketLog,
} from 'src/app/models';
import { TcTicketLogService } from 'src/app/shared/services';
import { SwitchThemeService } from '../../../../shared/components/switch-theme/switch-theme.service';
import { TicketLog } from '../../core/models/ticket.model';
import { TimeEntryHistoryComponent } from '../ticket-detail-modal/time-entry-history/time-entry-history.component';

/**
 * O que `TicketLogResource.php` realmente devolve: o `TicketLog` de `src/app/models` não declara
 * `task_id`, `system_id` nem `ticket_status_id`, embora a API mande os três.
 */
interface TicketLogPayload extends LegacyTicketLog {
  task_id?: string;
  system_id?: string;
  ticket_status_id?: string;
}

/**
 * Modal somente-leitura com o histórico de apontamentos de um chamado. Reusa a listagem do modal
 * do chamado (`TimeEntryHistoryComponent`) com `readOnly`, para as duas telas não divergirem.
 *
 * Busca por `ticketcenter/ticket-logs` e não por `TicketService.getById()`, que traria o
 * `TicketDetail` inteiro (todos os comentários, anexos e tarefas) só para ler `ticket_logs`.
 *
 * O tema é declarado no próprio diálogo porque a tela hospedeira é legada e não tem o wrapper
 * `.tw-light-theme` — sem isso ele abre sem cor. Mesmo cuidado do `ticket-detail-modal`.
 */
@Component({
  selector: 'app-time-entry-history-modal',
  standalone: true,
  imports: [DialogModule, ButtonModule, SkeletonModule, TimeEntryHistoryComponent],
  templateUrl: './time-entry-history-modal.component.html',
  styleUrls: ['./time-entry-history-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Sem escopo: o p-dialog com appendTo="body" sai da subárvore do host, então
  // regra escopada por atributo simplesmente não o alcança. Contido pela classe
  // `.time-entry-history-dialog` no topo do SCSS.
  encapsulation: ViewEncapsulation.None,
})
export class TimeEntryHistoryModalComponent implements OnChanges {
  private readonly ticketLogService = inject(TcTicketLogService);
  private readonly switchThemeService = inject(SwitchThemeService);

  private readonly currentTheme = toSignal(this.switchThemeService.currentTheme, {
    initialValue: this.switchThemeService.currentTheme.value,
  });

  @Input() ticketId: string | null = null;
  @Input() ticketNumber: string | null = null;
  @Input() visible = false;

  @Output() visibleChange = new EventEmitter<boolean>();

  readonly entries = signal<TicketLog[]>([]);
  readonly activeEntry = signal<TicketLog | null>(null);
  readonly loading = signal(false);
  readonly loadError = signal(false);

  ngOnChanges(changes: SimpleChanges): void {
    const becameVisible = changes['visible']?.currentValue && !changes['visible']?.previousValue;
    const ticketChanged =
      changes['ticketId'] && changes['ticketId'].currentValue !== changes['ticketId'].previousValue;

    if (this.visible && this.ticketId && (becameVisible || ticketChanged)) {
      void this.loadEntries(this.ticketId);
    }
  }

  onHide(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  /**
   * A classe de tema vai no `styleClass` do próprio diálogo porque o
   * `p-dialog` sai do lugar onde foi declarado no DOM — herdar a classe de um
   * ancestral não funciona.
   */
  getDialogStyleClass(): string {
    const themeClass = this.currentTheme() === 'dark' ? 'tw-dark-theme' : 'tw-light-theme';
    return `time-entry-history-dialog ${themeClass}`;
  }

  private async loadEntries(ticketId: string): Promise<void> {
    this.loading.set(true);
    this.loadError.set(false);
    this.entries.set([]);
    this.activeEntry.set(null);

    const findOptions: FindOptions = {
      perPage: 500,
      orderBy: 'start_date',
      orderDirection: 'desc',
      // `FilterOperator.has` manda a chave literal, sem prefixo — por isso o
      // `equal[...]` já vem escrito no nome. Mesma receita do modal legado de
      // apontamentos (`ticket-logs-modal.component.ts`).
      filters: [
        new FilterField({
          name: 'equal[ticket_id]',
          label: '',
          value: ticketId,
          operator: FilterOperator.has,
        }),
      ],
    };

    const response = await this.ticketLogService.findAll(findOptions).catch(() => null);

    // Uma resposta que não veio some da tela sem explicação se o estado vazio
    // for o mesmo de "chamado sem apontamento" — são mensagens opostas.
    if (!response) {
      this.loadError.set(true);
      this.loading.set(false);
      return;
    }

    const mapped = (response.data ?? []).map(log => this.toV3ndorLog(log as TicketLogPayload));
    this.entries.set(mapped);
    this.activeEntry.set(mapped.find(entry => !entry.end_date) ?? null);
    this.loading.set(false);
  }

  /**
   * Os dois `TicketLog` do projeto descrevem o mesmo registro com tipos diferentes (`Date | number`
   * no legado, `string | null` aqui). Converter, não castar: o cast calaria o compilador sem tornar
   * a data uma string de verdade.
   */
  private toV3ndorLog(log: TicketLogPayload): TicketLog {
    return {
      id: log.id ?? '',
      attendance_type: log.attendance_type,
      department_type: log.department_type,
      description: log.description,
      process: log.process,
      type: log.type,
      start_date: this.toIsoString(log.start_date),
      end_date: this.toIsoString(log.end_date),
      status: log.status?.description
        ? { id: log.status.id, description: log.status.description }
        : null,
      user: log.user ? { id: log.user.id, name: log.user.name ?? '' } : null,
      user_id: log.user_id,
      task: log.task?.id ? { id: log.task.id, description: log.task.description ?? '' } : null,
      task_id: log.task_id,
      system: log.system ? { id: log.system.id, description: log.system.description } : null,
      system_id: log.system_id,
      ticket_id: log.ticket_id,
      ticket_status_id: log.ticket_status_id,
      created_at: this.toIsoString(log.created_at) ?? '',
    };
  }

  private toIsoString(value: Date | number | string | null | undefined): string | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'string') return value;
    return new Date(value).toISOString();
  }
}
